/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */

import apiClient from './client';
import type { APIResponse } from '@/types';
import type {
  DashboardBalance,
  DashboardPayment,
  DashboardCustomer,
  DashboardPayout,
  DashboardConnectedAccount,
  DashboardPagedResult,
} from '@/types/stripeDashboard';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const unwrap = <T>(data: any): T => data?.result ?? data;

const BASE = '/stripe-dashboard';

export const stripeDashboardApi = {
  getBalance: async (accountId?: string): Promise<DashboardBalance> => {
    const res = await apiClient.get<APIResponse<DashboardBalance>>(
      `${BASE}/balance`, { params: accountId ? { accountId } : {} });
    return unwrap(res.data);
  },
  listPayments: async (p: {
    limit?: number; startingAfter?: string; status?: string;
    customerId?: string; dateFrom?: number; dateTo?: number; accountId?: string;
  }): Promise<DashboardPagedResult<DashboardPayment>> => {
    const res = await apiClient.get<APIResponse<DashboardPagedResult<DashboardPayment>>>(
      `${BASE}/payments`, { params: p });
    return unwrap(res.data);
  },
  getPayment: async (id: string, accountId?: string): Promise<DashboardPayment> => {
    const res = await apiClient.get<APIResponse<DashboardPayment>>(
      `${BASE}/payments/${id}`, { params: { accountId } });
    return unwrap(res.data);
  },
  listCustomers: async (p: {
    limit?: number; startingAfter?: string; email?: string; accountId?: string;
  }): Promise<DashboardPagedResult<DashboardCustomer>> => {
    const res = await apiClient.get<APIResponse<DashboardPagedResult<DashboardCustomer>>>(
      `${BASE}/customers`, { params: p });
    return unwrap(res.data);
  },
  getCustomerPayments: async (id: string, limit = 50, accountId?: string): Promise<DashboardPayment[]> => {
    const res = await apiClient.get<APIResponse<DashboardPayment[]>>(
      `${BASE}/customers/${id}/payments`, { params: { limit, accountId } });
    return unwrap(res.data);
  },
  listPayouts: async (p: {
    limit?: number; startingAfter?: string; status?: string;
    dateFrom?: number; dateTo?: number; accountId?: string;
  }): Promise<DashboardPagedResult<DashboardPayout>> => {
    const res = await apiClient.get<APIResponse<DashboardPagedResult<DashboardPayout>>>(
      `${BASE}/payouts`, { params: p });
    return unwrap(res.data);
  },
  listAccounts: async (p: {
    limit?: number; startingAfter?: string;
  }): Promise<DashboardPagedResult<DashboardConnectedAccount>> => {
    const res = await apiClient.get<APIResponse<DashboardPagedResult<DashboardConnectedAccount>>>(
      `${BASE}/accounts`, { params: p });
    return unwrap(res.data);
  },
  getAccountBalance: async (id: string): Promise<DashboardBalance> => {
    const res = await apiClient.get<APIResponse<DashboardBalance>>(
      `${BASE}/accounts/${id}/balance`);
    return unwrap(res.data);
  },
  getMyAccountId: async (): Promise<string | null> => {
    const res = await apiClient.get<APIResponse<string | null>>(
      `${BASE}/my-account-id`);
    return unwrap(res.data);
  },
  withdrawBalance: async (): Promise<{
    status: string; payoutId?: string; amountCents?: number;
    currency?: string; arrivalDate?: string; message?: string;
  }> => {
    const res = await apiClient.post(`${BASE}/withdraw`);
    return unwrap(res.data);
  },
  platformWithdrawBalance: async (): Promise<{
    status: string; payoutId?: string; amountCents?: number;
    currency?: string; arrivalDate?: string; message?: string;
  }> => {
    const res = await apiClient.post(`${BASE}/platform-withdraw`);
    return unwrap(res.data);
  },
};

export const stripeDashboardQueryKeys = {
  all: ['stripe-dashboard'] as const,
  balance: (accountId?: string) => [...stripeDashboardQueryKeys.all, 'balance', accountId ?? 'platform'] as const,
  payments: (p: object) => [...stripeDashboardQueryKeys.all, 'payments', p] as const,
  payment: (id: string) => [...stripeDashboardQueryKeys.all, 'payment', id] as const,
  customers: (p: object) => [...stripeDashboardQueryKeys.all, 'customers', p] as const,
  customerPayments: (id: string) => [...stripeDashboardQueryKeys.all, 'customer-payments', id] as const,
  payouts: (p: object) => [...stripeDashboardQueryKeys.all, 'payouts', p] as const,
  accounts: (p: object) => [...stripeDashboardQueryKeys.all, 'accounts', p] as const,
  accountBalance: (id: string) => [...stripeDashboardQueryKeys.all, 'account-balance', id] as const,
  myAccountId: [...['stripe-dashboard'], 'my-account-id'] as const,
};
